import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { Account } from '@mitel/cloudlink-sdk';
import { Util } from './../../../../../core/utils/Util';
import { CustomValidators } from './../../../../../core/utils/CustomValidators';

export interface UnifyPhoneConfigForm {
  useExistingTenant: FormControl<boolean>;
  apiKey: FormControl<string>;
  tenantName: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phoneNumber: FormControl;
};

export enum tenantOrigin {
  PROVISIONED = 'provisioned',
  GENERATED = 'generated',
  TRUSTED = 'trusted'
};

@Component({
  selector: 'app-unify-phone-dialog',
  templateUrl: './unify-phone-dialog.component.html',
  styleUrls: ['./unify-phone-dialog.component.scss']
})
export class UnifyPhoneDialogComponent implements OnInit {
  showFormError: boolean = false;
  errMsg: string = '';

  configForm: FormGroup<UnifyPhoneConfigForm>;
  product: any;
  account: Account;
  tenant: any;
  editable: boolean = true;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<UnifyPhoneDialogComponent>,
    private formBuilder: FormBuilder,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private translateService: TranslateService) {
    this.matIconRegistry.addSvgIcon(
      'trash_can',
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash.svg")
    );
  }

  async ngOnInit() {
    this.configForm = this.formBuilder.group({
      useExistingTenant: this.formBuilder.control(false, []),
      apiKey: this.formBuilder.control('', []),
      tenantName: this.formBuilder.control('', []),
      firstName: this.formBuilder.control('', []),
      lastName: this.formBuilder.control('', []),
      email: this.formBuilder.control('', [CustomValidators.validateEmail]),
      phoneNumber: this.formBuilder.control('', [])
    });

    this.product = this.data.product;
    this.account = this.data.account;
    this.editable = !this.product.setupComplete;

    //setupComplete is complete so get tenant to populate fields
    if (!this.editable) {
      try {
        this.tenant = await this.product.service.getUnifyPhoneTenant(this.account);
        if (this.tenant?.unifyPhoneTenant) {
          this.setFormWithFromTenant();
        } else {
          this.errMsg = 'integrations.unify-phone.config-dialog.unable-retrieve-tenant';
          this.showFormError = true;
        }
      } catch (error) {
        this.errMsg = 'integrations.unify-phone.config-dialog.unable-retrieve-tenant';
        this.showFormError = true;
      }
      this.disableFormFields();
    }
  }

  useExistingTenantChanged() {
    let useExistingTenant = this.configForm.controls['useExistingTenant']?.value;
    if (useExistingTenant) {
      this.enableControl('apiKey');
      this.disableControl('tenantName');
      this.disableControl('firstName');
      this.disableControl('lastName');
      this.disableControl('email');
      this.disableControl('phoneNumber');
    } else {
      this.disableControl('apiKey');
      this.enableControl('tenantName');
      this.enableControl('firstName');
      this.enableControl('lastName');
      this.enableControl('email');
      this.enableControl('phoneNumber');
    }
  }

  enableControl(fieldName: string) {
    this.configForm.controls[fieldName]?.enable();
    this.configForm.controls[fieldName]?.markAsUntouched();
  }

  disableControl(fieldName: string) {
    this.configForm.controls[fieldName]?.setValue('');
    this.configForm.controls[fieldName]?.disable();
  }

  setFormWithFromTenant() {
    this.configForm?.patchValue({
      tenantName: { ...this.tenant?.unifyPhoneTenant }.tenantName || '',
      firstName: { ...this.tenant?.unifyPhoneTenant }.supportContactFirstName || '',
      lastName: { ...this.tenant?.unifyPhoneTenant }.supportContactLastName || '',
      email: { ...this.tenant?.unifyPhoneTenant }.supportContactEmailAddress || '',
      phoneNumber: { ...this.tenant?.unifyPhoneTenant }.supportContactPhoneNumber || ''
    });
  }

  disableFormFields() {
    this.configForm.controls['tenantName'].disable();
    this.configForm.controls['firstName'].disable();
    this.configForm.controls['lastName'].disable();
    this.configForm.controls['email'].disable();
    this.configForm.controls['phoneNumber'].disable();
  }

  onRemoveIntegration() {
    this.dialogRef.close({ remove: true });
  }

  async onSave() {
    this.showFormError = false;
    this.errMsg = '';

    const provisioned = !!this.configForm.controls.useExistingTenant.value;

    const unifyPhoneTenant = {
      tenantOrigin: provisioned ? tenantOrigin.PROVISIONED : tenantOrigin.GENERATED,
      ...(provisioned && { apiKey: this.configForm.controls.apiKey.value }),
      ...(!provisioned && { tenantName: this.configForm.controls.tenantName.value.trim() }),
      ...(!provisioned && { supportContactFirstName: this.configForm.controls.firstName.value.trim() }),
      ...(!provisioned && { supportContactLastName: this.configForm.controls.lastName.value.trim() }),
      ...(!provisioned && { supportContactEmailAddress: this.configForm.controls.email.value }),
      ...(!provisioned && { supportContactPhoneNumber: this.configForm.controls.phoneNumber.value?.e164Number }),
    };

    try {
      let result = await this.product.service.saveConfigurationData(this.data.account, unifyPhoneTenant);
      this.dialogRef.close({ setupComplete: result });
    } catch (error) {
      // e.g
      // { "message": "error creating tenant - {\"errors\":[{\"path\":\"/body/firstName\",\"message\":\"must have required property 'firstName'\",\"errorCode\":\"required.openapi.validation\"}],\"message\":\"request/body must have required property 'firstName'\"}"}
      // { "message": "error creating tenant - {\"errorCode\":\"TENANT_NAME_ALREADY_EXISTS\",\"errorDescription\":\"Tenant name aldready exists in the db.\"}"

      let message = '';
      let errorMessage = Util.extractMessageFromError(error)?.replace(/\\"/g, '') ?? error;
      try {
        var splitted = errorMessage.split(' -');
        let jsonErrorDetails = JSON.parse(splitted[1]);
        message = ' : ' + splitted[0] + ' - ' + (jsonErrorDetails.message ?? jsonErrorDetails.errorDescription);
      } catch (error) {
        message = ' : ' + errorMessage;
      }

      this.errMsg = this.translateService.instant('integrations.unify-phone.config-dialog.error-saving-configuration') + message;
      this.showFormError = true;
      console.log(error);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }
}
